import { union } from "lodash";
import { IFilterItem } from "../generic/generic-types";
import { maxSelectedClients } from "../utils/clientlistHelper";
import { IClientAccount, IClientAccountFromList } from "../utils/types";
import {
  ISalesCreditProps,
  ISalesCreditWidgetProps,
} from "./salescredit-types";

export const SalesCreditQueryBuilder = {
  BuildStandardFilterPropQuery,
  BuildStandardQuery,
  BuildTopTenSalesQuery,
  HasWidgetPropChanged,
  PropHasChanged,
};

type salescreditFilter =
  | string
  | string[]
  | IClientAccount[]
  | IClientAccountFromList[];

function HasWidgetPropChanged(
  existing: ISalesCreditWidgetProps,
  newProps: ISalesCreditWidgetProps
) {
  return (
    existing.filters !== newProps.filters ||
    existing.headers !== newProps.headers ||
    existing.userEmail !== newProps.userEmail
  );
}

function PropHasChanged(
  existing: ISalesCreditProps,
  newProps: ISalesCreditProps
) {
  return (
    existing.date !== newProps.date ||
    existing.filterComClient_groupId !== newProps.filterComClient_groupId ||
    existing.filterComClient_leComCtryName !==
      newProps.filterComClient_leComCtryName ||
    existing.filterComClient_leId !== newProps.filterComClient_leId ||
    existing.filterComClient_leTypoL2 !== newProps.filterComClient_leTypoL2 ||
    existing.filterFpv_buMnemo !== newProps.filterFpv_buMnemo ||
    existing.filterFpv_pcMnemo !== newProps.filterFpv_pcMnemo ||
    existing.filterFpv_sbuMnemo !== newProps.filterFpv_sbuMnemo ||
    existing.filterProduct_productL1 !== newProps.filterProduct_productL1 ||
    existing.filterProduct_productL4 !== newProps.filterProduct_productL4 ||
    existing.filterSales_salesName !== newProps.filterSales_salesName ||
    existing.filterSales_teamName !== newProps.filterSales_teamName ||
    existing.userEmail !== newProps.userEmail ||
    existing.periodType !== newProps.periodType
  );
}

// Allways returns the same values ensures client the client request more information than is required
// by any one widget however this saves on muiltiple requests
function BuildStandardQuery(date: Date, stepOne: any, isSalesExec: any) {
  return {
    date: date.toString(),
    onlyStepGroup1: stepOne,
    salesExec: isSalesExec,
    withCashEquity: true,
    withCashEquity_Y_1: true,
    withMtd: true,
    withSalesCreditComponents: true,
    withSalesCreditComponents_Y_1: true,
    withVoiceElec: true,
    withVoiceElec_Y_1: true,
    withWeeklyKpis: true,
    withY_2: true,
  };
}

function BuildStandardFilterPropQuery(
  filterProps: ISalesCreditProps,
  stepOne: any,
  isSalesExec: any
) {
  if (!filterProps.date) {
    return null;
  }
  return {
    date: filterProps.date.toString(),
    "filterFpv.pcMnemo": filterProps.filterFpv_pcMnemo,
    "filterFpv.sbuMnemo": filterProps.filterFpv_sbuMnemo,
    "filterProduct.productL1": filterProps.filterProduct_productL1,
    "filterProduct.productL4": filterProps.filterProduct_productL4,
    "filterSales.salesName": filterProps.filterSales_salesName,
    "filterSales.teamName": filterProps.filterSales_teamName,
    onlyStepGroup1: stepOne,
    salesExec: isSalesExec,
    topN: filterProps.topN,
    withCashEquity: true,
    withCashEquity_Y_1: true,
    withMtd: true,
    withSalesCreditComponents: true,
    withSalesCreditComponents_Y_1: true,
    withVoiceElec: true,
    withVoiceElec_Y_1: true,
    withWeeklyKpis: true,
    withY_2: true,
  };
}

export function BuildTopTenSalesQuery(
  filterProps: ISalesCreditProps,
  stepOne: any,
  isSalesExec: any
) {
  if (!filterProps.date) {
    return null;
  }
  return {
    date: filterProps.date.toString(),
    "filterComClient.groupId": filterProps.filterComClient_groupId,
    "filterComClient.leComCtryName": filterProps.filterComClient_leComCtryName,
    "filterComClient.leId": filterProps.filterComClient_leId,
    "filterComClient.leTypoL2": filterProps.filterComClient_leTypoL2,
    "filterFpv.buMnemo": filterProps.filterFpv_buMnemo,
    "filterFpv.pcMnemo": filterProps.filterFpv_pcMnemo,
    "filterFpv.sbuMnemo": filterProps.filterFpv_sbuMnemo,
    "filterProduct.productL1": filterProps.filterProduct_productL1,
    "filterProduct.productL4": filterProps.filterProduct_productL4,
    "filterSales.salesName": filterProps.filterSales_salesName,
    "filterSales.teamName": filterProps.filterSales_teamName,
    onlyStepGroup1: stepOne,
    salesExec: isSalesExec,
    topN: 10,
    withCashEquity: false,
    withCashEquity_Y_1: false,
    withMtd: false,
    withSalesCreditComponents: false,
    withSalesCreditComponents_Y_1: false,
    withVoiceElec: false,
    withVoiceElec_Y_1: false,
    withWeeklyKpis: false,
    withY_2: false,
  };
}

export function CreateDateFromFilter(filterProperties: {
  [attribute: string]: string[];
}) {
  const dateKey = "Date";
  const date = filterProperties[dateKey]
    ? filterProperties[dateKey][0]
    : undefined;
  if (date !== undefined) {
    const dateReturn = date.toString();
    if (dateReturn.length > 10) {
      return date.slice(0, 10);
    }
    return dateReturn;
  } else {
    return new Date().toISOString().slice(0, 10);
  }
}

export function BuildSalesCreditFilters(filterProperties: {
  [attribute: string]: string[];
}): IFilterItem[] {
  const filters: IFilterItem[] = [];
  for (const key of Object.keys(filterProperties)) {
      if (
        filterProperties[key] !== undefined &&
        filterProperties[key] !== null &&
        filterProperties[key].length > 0
      ) {
        if (key === "Date" || key === "columns" || key === "text") {
          continue;
        }
        if (
          key === "logicalAttributesFilter" &&
          filterProperties[key].toString() !== ""
        ) {
          filters.push({
            attribute: key,
            equals: true,
            value: filterProperties[key].toString(),
          });
          continue;
        }
        if (key === "accounts") {
          const uniques = union(filterProperties[key]);
          const slicedStrings = uniques.slice(0, maxSelectedClients);
          filterProperties["clientgen_id"] = slicedStrings;
          filters.push({
            attribute: "clientgen_id",
            equals: true,
            value: BuildAccountValues(filterProperties[key]),
          });

          continue;
        }
        if (key === "clientgen_id") {
          const uniques = union(filterProperties[key]);
          const slicedStrings = uniques.slice(0, maxSelectedClients);
          filterProperties["clientgen_id"] = slicedStrings;

          filters.push({
            attribute: "clientgen_id",
            equals: true,
            value: BuildAccountValues(filterProperties[key]),
          });

          continue;
        }

        filters.push({
          attribute: key,
          equals: true,
          value: BuildValues(filterProperties[key]),
        });
      }
  }
  return filters;
}
function BuildAccountValues(values: salescreditFilter): string {
  const accountFilter: string[] = [];

  for (const account of values as string[]) {
    accountFilter.push(account);
  }

  if (accountFilter.length === 1) {
    return accountFilter[0];
  }
  return accountFilter.join("+");
}

function BuildValues(values: any[]) {
  if (values.length === 1) {
    return ReplaceInvalidCharacters(values[0]);
  }
  return ReplaceInvalidCharacters(values.join("+"));
}

// replaces , inside values with **
function ReplaceInvalidCharacters(valueString: string) {
  const re = /\,/gi;
  return valueString.replace(re, "~,");
}
